/**
 * O Conferir e o Acompanhar por CCB declarada (E177.F5, GUS-253, ADR-0130; HTML 6.8 por CCB). Regra
 * pura, sem JSX.
 *
 * - **Contagens**: os documentos de cada CCB (os de origem e os vinculados) e, deles, os
 *   compartilhados — os que outra CCB do envio também leva. A mesma régua do recibo dos casos no
 *   servidor (`contagens_do_contrato`).
 * - **Cada documento** diz se é "Somente esta CCB", "Compartilhado com" outras ou "Compartilhado ·
 *   enviado na" CCB de origem.
 * - **Conflito**: a CCB com o identificador do pacote divergente e ainda não mantida segura o
 *   "Concluir envio" até a pessoa corrigir o número ou manter o informado.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ContratoDoEnvioData, EnvioDaAberturaData } from '@/lib/abertura_do_caso';

import { compartilhadaCom, nomeDoArquivo, type BlocoDaCcb } from './blocosDasCcbs';

const t = ptBR.abertura.conferirPorCcb;
const rotulo = (numero: string): string =>
  ptBR.abertura.contratosDigitais.rotulo.replace('{numero}', numero);

/** O envio com CCB declarada: o Conferir agrupa por CCB. */
export function envioPorCcb(envio: EnvioDaAberturaData | null | undefined): boolean {
  return (envio?.contratos ?? []).some((c) => c.ccb != null);
}

export interface Contagens {
  documentos: number;
  compartilhados: number;
}

/** Os documentos do bloco e, deles, os que outro bloco também leva. */
export function contagensDoBloco(blocos: readonly BlocoDaCcb[], bloco: BlocoDaCcb): Contagens {
  const deles = new Set([
    ...bloco.unidades.map((u) => u.documento_id),
    ...bloco.vinculadas.map((v) => v.unidade.documento_id),
  ]);
  const dosOutros = new Set(
    blocos
      .filter((b) => b.chave !== bloco.chave)
      .flatMap((b) => [
        ...b.unidades.map((u) => u.documento_id),
        ...b.vinculadas.map((v) => v.unidade.documento_id),
      ]),
  );
  return {
    documentos: deles.size,
    compartilhados: [...deles].filter((id) => dosOutros.has(id)).length,
  };
}

/** "2 documentos · 1 compartilhado"; sem compartilhado, só os documentos. */
export function textoDasContagens({ documentos, compartilhados }: Contagens): string {
  const docs = documentos === 1 ? t.documentoUm : t.documentos.replace('{n}', String(documentos));
  if (compartilhados === 0) return docs;
  const comp =
    compartilhados === 1
      ? t.compartilhadoUm
      : t.compartilhados.replace('{n}', String(compartilhados));
  return `${docs} · ${comp}`;
}

/** A linha da CCB na tabela: as contagens e, quando há, o identificador divergente. */
export function resumoDaCcb(contrato: ContratoDoEnvioData, contagens: Contagens): string {
  const partes = [textoDasContagens(contagens)];
  const divergencia = contrato.ccb?.divergencia ?? null;
  if (divergencia !== null) {
    // E177.F5.C01 (GUS-253 item 4): a mantida diz o que o pacote indica.
    partes.push(
      divergencia.mantida
        ? t.divergenteMantido.replace('{lido}', divergencia.lida.valor)
        : t.divergente,
    );
  }
  return partes.join(' · ');
}

export interface DocumentoDoGrupo {
  documentoId: string;
  nome: string;
  detalhe: string;
}

/** Os documentos do grupo da CCB, cada um com o que diz do compartilhamento; o homônimo de outro
 * documento leva o identificador curto (E177.F4.C02). */
export function documentosDoGrupo(
  blocos: readonly BlocoDaCcb[],
  bloco: BlocoDaCcb,
): DocumentoDoGrupo[] {
  const usos = compartilhadaCom(blocos, bloco);
  const deOrigem = bloco.unidades.map((u) => {
    const outras = usos.get(u.documento_id) ?? [];
    return {
      documentoId: u.documento_id,
      nome: nomeDoArquivo(blocos, u),
      detalhe:
        outras.length === 0
          ? t.somenteEsta
          : t.compartilhadoCom.replace('{ccbs}', outras.map((c) => rotulo(c.numero)).join(', ')),
    };
  });
  const vinculadas = bloco.vinculadas.map(({ unidade, origem, nomeNaOrigem }) => ({
    documentoId: unidade.documento_id,
    nome: nomeDoArquivo(blocos, unidade),
    detalhe:
      origem === null
        ? t.vinculadoAqui
        : (nomeNaOrigem === null
            ? t.enviadoNa
            : t.enviadoNaComNome.replace('{nome}', nomeNaOrigem)
          ).replace('{ccb}', rotulo(origem.numero)),
  }));
  return [...deOrigem, ...vinculadas];
}

/** As CCBs com o identificador do pacote divergente que a pessoa ainda não manteve. */
export function divergentesSemDecisao(
  contratos: readonly ContratoDoEnvioData[],
): ContratoDoEnvioData[] {
  return contratos.filter((c) => c.ccb?.divergencia != null && !c.ccb.divergencia.mantida);
}

/** "CCB 4201 · 2 documentos" — o título do grupo recolhido. */
export function tituloDoGrupo(bloco: BlocoDaCcb, contagens: Contagens): string {
  const documentos =
    contagens.documentos === 1
      ? t.documentoUm
      : t.documentos.replace('{n}', String(contagens.documentos));
  return t.grupo
    .replace('{ccb}', rotulo(bloco.ccb?.numero ?? ''))
    .replace('{documentos}', documentos);
}
